import { useMemo } from "react"
import { useCurrentFrame } from "remotion"
import { z } from "zod"

import { savedInputPropsSchema } from "@postsunday/remotion-shared/lib/validations/input-props"
import { DEFAULT_FPS } from "@postsunday/shared/lib/constants/server"
import { grabWordsAtATime } from "@postsunday/shared/lib/utils/words/grabWordsAtATime"

export const useCurrentScene = ({
  chunks,
  startFromFrame,
  endFrame,
  wordsAtATime = 4,
}: {
  chunks: z.infer<
    typeof savedInputPropsSchema
  >["clippingBasedProperties"]["chunks"]
  startFromFrame: number
  endFrame: number
  wordsAtATime?: number
}) => {
  const frame = useCurrentFrame()

  // Get all words that will be spoken
  const words = chunks
    .flatMap(({ words }) => words.map((word) => word))
    .filter(
      // if the word.endFrame is after the startFromFrame OR word.startFrame is before the endFrame
      (word) =>
        startFromFrame <= Math.ceil(word.end * DEFAULT_FPS) ||
        Math.floor(word.start * DEFAULT_FPS) <= endFrame
    )

  // grab words at a time, but stop grabbing if it's a new sentence
  // this avoids "reading ahead" at pauses
  const subtitles = useMemo(
    () => grabWordsAtATime(words, wordsAtATime),
    [words, wordsAtATime]
  )

  // this should be true
  const firstWordStartFrame = Math.ceil(
    (chunks?.at(0)?.words?.at(0)?.end ?? 0) * DEFAULT_FPS
  )
  const shouldDisplayFirstSub = frame + startFromFrame <= firstWordStartFrame

  const currentScene = useMemo(
    () =>
      subtitles.find((sub) => {
        const firstWord = sub.at(0)
        const lastWord = sub.at(-1)
        if (!firstWord || !lastWord) return false
        const elapsed = startFromFrame + frame
        const firstWordFrame = firstWord.start * DEFAULT_FPS
        const lastWordFrame = lastWord.end * DEFAULT_FPS

        const isCurrentSubtitle =
          elapsed >= firstWordFrame - 1 && elapsed <= lastWordFrame

        return isCurrentSubtitle
      }) ?? (shouldDisplayFirstSub ? subtitles[0] : []),
    [subtitles, startFromFrame, frame, shouldDisplayFirstSub]
  )

  // can be -1 is before first word OR after last
  const currentWordIndexInCurrentScene = currentScene?.findIndex(
    (word) =>
      word.start * DEFAULT_FPS <= frame + startFromFrame &&
      word.end * DEFAULT_FPS >= frame + startFromFrame
  )

  // can be -1 is before first word OR after last
  const lastWordIndexInCurrentScene = currentScene?.findLastIndex(
    (word) => word.start * DEFAULT_FPS <= frame + startFromFrame
  )

  return {
    currentScene,
    currentWordIndexInCurrentScene,
    lastWordIndexInCurrentScene,
    frame,
  }
}
